import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Hub } from "aws-amplify/utils";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiRequestError } from "@/lib/api";
import { currentEmail, signOut } from "@/lib/auth";
import { DatabasesPage } from "@/pages/databases";
import { SignInPage } from "@/pages/sign-in";

type Session = { status: "loading" } | { status: "signed-out" } | { status: "signed-in"; email: string };

/**
 * Shows the sign-in page or the signed-in view. Amplify keeps the tokens in local storage and refreshes
 * them, so a reload or a new browser session within the refresh token's 30 days stays signed in.
 */
export function App() {
  const [session, setSession] = useState<Session>({ status: "loading" });

  const refresh = useCallback(async () => {
    const email = await currentEmail();
    setSession(email ? { status: "signed-in", email } : { status: "signed-out" });
  }, []);

  const queryClient = useMemo(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          // The API no longer accepts the session, for example after the refresh token was revoked elsewhere.
          onError: (error) => {
            if (error instanceof ApiRequestError && error.status === 401) void signOut().finally(refresh);
          },
        }),
        defaultOptions: {
          queries: {
            retry: (count, error) => !(error instanceof ApiRequestError && error.status < 500) && count < 2,
            refetchOnWindowFocus: false,
          },
        },
      }),
    [refresh],
  );

  useEffect(() => {
    void refresh();
    return Hub.listen("auth", ({ payload }) => {
      if (payload.event === "signedIn" || payload.event === "signedOut" || payload.event === "tokenRefresh_failure") void refresh();
    });
  }, [refresh]);

  const onSignOut = useCallback(async () => {
    await signOut();
    queryClient.clear();
    setSession({ status: "signed-out" });
  }, [queryClient]);

  return (
    <QueryClientProvider client={queryClient}>
      {session.status === "loading" ? (
        <div className="min-h-svh bg-surface" aria-busy="true" />
      ) : session.status === "signed-in" ? (
        <DatabasesPage email={session.email} onSignOut={onSignOut} />
      ) : (
        <SignInPage onSignedIn={() => void refresh()} />
      )}
    </QueryClientProvider>
  );
}
